import { createHeaders } from "ComponentHelpers/HeaderHelper";
import { findMissingDaysBetween, maxBy, minBy } from "ComponentHelpers/TimetableHelper";
import { DynamicSettings } from "Components/admin/DynamicSettings";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "Shared/haunted/CustomHooks";
import { removeTrailingSlash } from "Services/common";
import { API_TIMETABLE_URL } from "Services/constants";
import { useEffectWithoutCleanup } from "Shared/haunted/CustomHooks";
import { CurrentSearchManager } from "Shared/managers/current-search-manager/useCurrentSearchManager";
import { ApiSchedule } from "Shared/models/ApiSchedule";
import { ApiTimeTableV2 } from "Shared/models/ApiTimeTableV2";
import { Schedule } from "Shared/models/Schedule";
import { getDefaultTimetable } from "Shared/models/Timetable";
import { useReduxState } from "Shared/redux/useReduxState";

export interface Props {
	startLoading: () => LoaderInstance;
	stopLoading: (loader: LoaderInstance) => void;
	currentSearchManager: CurrentSearchManager;
	dynamicSettings: DynamicSettings;
}

export interface Timetable {
	disabledDepartureDates: dayjs.Dayjs[];
	disabledReturnDates: dayjs.Dayjs[];
	isLoading: boolean;
	maxDepartureDate: dayjs.Dayjs;
	maxReturnDate: dayjs.Dayjs;
	minDepartureDate: dayjs.Dayjs;
	minReturnDate: dayjs.Dayjs;
	outboundAvailableDates: Schedule[];
	inboundAvailableDates: Schedule[];
	selectedPriceOutbound: string | number;
	selectedPriceInbound: string | number;
	setMinReturnDate: (date: dayjs.Dayjs) => void;
}

interface TimetableQuerySignature {
	originCode: string | undefined;
	destinationCode: string | undefined;
	isOneWay: boolean;
}

export const useTimetable = (props: Props): Timetable => {
	const [navitaireSettings] = useReduxState("navitaireSettings");

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [disabledDepartureDates, setDisabledDepartureDates] = useState<dayjs.Dayjs[]>([]);
	const [disabledReturnDates, setDisabledReturnDates] = useState<dayjs.Dayjs[]>([]);
	const [maxDepartureDate, setMaxDepartureDate] = useState<dayjs.Dayjs>(undefined);
	const [maxReturnDate, setMaxReturnDate] = useState<dayjs.Dayjs>(undefined);
	const [minDepartureDate, setMinDepartureDate] = useState<dayjs.Dayjs>(undefined);
	const [minReturnDate, setMinReturnDate] = useState<dayjs.Dayjs>(undefined);
	const [outboundAvailableDates, setOutboundAvailableDates] = useState<Schedule[]>([]);
	const [inboundAvailableDates, setInboundAvailableDates] = useState<Schedule[]>([]);
	const [lastQueriedTimetable, setLastQueriedTimetable] = useState<TimetableQuerySignature>({
		originCode: undefined,
		destinationCode: undefined,
		isOneWay: false,
	});

	const digestTimetable = async (info: Schedule[]) => {
		let disabled: dayjs.Dayjs[] = [];

		info = info.sort((a, b) => (a.departureDate.isAfter(b.departureDate) ? 1 : -1));

		const min = minBy(info, (i) => i.departureDate.unix())?.departureDate;
		const max = maxBy(info, (i) => i.departureDate.unix())?.departureDate;

		disabled = findMissingDaysBetween(
			info.map((i): Dayjs => i.departureDate),
			min || dayjs(),
			max
		);

		return { min: min || dayjs(), max: max || dayjs(), disabled };
	};

	const createTimetableUrl = () => {
		const startOfMonth = dayjs().startOf("month").format("YYYY-MM-DD");
		const shouldSearchOneWayOnly =
			!window.JetSmart.TimetableOptions.IncludeReturnFlights || props.currentSearchManager.currentSearch.isOneWay;

		if (window.JetSmart.TimetableOptions.Mode === "DirectFareCache") {
			const baseUrl = removeTrailingSlash(window.JetSmart.TimetableOptions.BaseAddress);

			return [
				`${baseUrl}/timetable?departure=${props.currentSearchManager.currentSearch.originCity.code}`,
				`destination=${props.currentSearchManager.currentSearch.destinationCity.code}`,
				`currency=${navitaireSettings?.currency}`,
				`withPriceRange`,
				`startDate=${startOfMonth}`,
				`${shouldSearchOneWayOnly ? "return=false" : ""}`,
			]
				.filter((item) => item)
				.join("&");
		}

		return [
			`${API_TIMETABLE_URL}?departure=${props.currentSearchManager.currentSearch.originCity.code}`,
			`destination=${props.currentSearchManager.currentSearch.destinationCity.code}`,
			`currency=${navitaireSettings?.currency}`,
			`startDate=${startOfMonth}`,
			`${shouldSearchOneWayOnly ? "return=false" : ""}`,
		]
			.filter((item) => item)
			.join("&");
	};

	const mapToOffer = (o: ApiSchedule): Schedule => ({
		currency: o.currency,
		departureDate: dayjs(o.departureDate),
		price: o.price,
		priceCategory: o.priceCategory,
	});

	const constrainDepartureDates = async (schedule: Schedule[]) => {
		const outbound = await digestTimetable(schedule);

		setDisabledDepartureDates(outbound.disabled);
		setMaxDepartureDate(dayjs(outbound.max));
		setMinDepartureDate(dayjs(outbound.min));
	};

	const constrainReturnDates = async (schedule: Schedule[]) => {
		const inbound = await digestTimetable(schedule);

		const isOutboundDateLaterThanMinInboundDate =
			props.currentSearchManager.currentSearch.departureDate &&
			inbound.min.isSameOrBefore(props.currentSearchManager.currentSearch.departureDate, "day");

		setDisabledReturnDates(inbound.disabled);
		setMaxReturnDate(dayjs(inbound.max));
		setMinReturnDate(
			isOutboundDateLaterThanMinInboundDate
				? dayjs(props.currentSearchManager.currentSearch.departureDate)
				: dayjs(inbound.min)
		);
	};

	const update = async () => {
		if (
			!props.currentSearchManager.currentSearch.originCity ||
			!props.currentSearchManager.currentSearch.destinationCity
		) {
			return;
		}

		const newQuerySignature: TimetableQuerySignature = {
			originCode: props.currentSearchManager.currentSearch.originCity.code,
			destinationCode: props.currentSearchManager.currentSearch.destinationCity.code,
			isOneWay: props.currentSearchManager.currentSearch.isOneWay,
		};

		const isSameQuerySignature =
			lastQueriedTimetable.originCode === newQuerySignature.originCode &&
			lastQueriedTimetable.destinationCode === newQuerySignature.destinationCode &&
			lastQueriedTimetable.isOneWay === newQuerySignature.isOneWay;

		if (isSameQuerySignature) return;

		setLastQueriedTimetable(newQuerySignature);

		if (!props.currentSearchManager.currentSearch.isReloading) {
			const onlyIsOneWayChanged =
				lastQueriedTimetable.originCode === newQuerySignature.originCode &&
				lastQueriedTimetable.destinationCode === newQuerySignature.destinationCode &&
				lastQueriedTimetable.isOneWay !== newQuerySignature.isOneWay;

			if (!onlyIsOneWayChanged) {
				props.currentSearchManager.setDepartureDate(undefined);
			}

			props.currentSearchManager.setReturnDate(undefined);
		}

		setIsLoading(true);
		const loader = props.startLoading();

		const timetable = await loadTimetable();

		if (!timetable) {
			// eslint-disable-next-line no-console
			console.error("Failed to load timetable data");
			setIsLoading(false);
			props.stopLoading(loader);
			return;
		}

		setOutboundAvailableDates(timetable.outbound);
		setInboundAvailableDates(timetable.inbound);

		await constrainDepartureDates(timetable.outbound);
		if (!props.currentSearchManager.currentSearch.isOneWay) await constrainReturnDates(timetable.inbound);

		setIsLoading(false);
		props.stopLoading(loader);

		props.currentSearchManager.setIsReloading(false);
	};

	const loadTimetable = async () => {
		const headers = createHeaders();

		const url = createTimetableUrl();
		const fetchOptions: RequestInit = {
			method: "GET",
			mode: "cors",
			headers,
		};

		try {
			const result = await fetch(url, fetchOptions);

			if (result.ok) {
				const resultJson = (await result.json()) as ApiTimeTableV2;

				return {
					inbound: resultJson.inbound?.map(mapToOffer) || [],
					outbound: resultJson.outbound?.map(mapToOffer) || [],
				};
			}

			return getDefaultTimetable(props.dynamicSettings.TimetableMaxMonthsFallback);
		} catch (e) {
			return getDefaultTimetable(props.dynamicSettings.TimetableMaxMonthsFallback);
		}
	};

	useEffectWithoutCleanup(update, [
		props.currentSearchManager.currentSearch.originCity?.code,
		props.currentSearchManager.currentSearch.destinationCity?.code,
		props.currentSearchManager.currentSearch.isOneWay,
	]);

	useEffectWithoutCleanup(() => {
		if (props.currentSearchManager.currentSearch.isOneWay) return;
		if (!props.currentSearchManager.currentSearch.departureDate || inboundAvailableDates.length === 0) return;
		void constrainReturnDates(inboundAvailableDates);
	}, [
		props.currentSearchManager.currentSearch.departureDate,
		props.currentSearchManager.currentSearch.isOneWay,
		inboundAvailableDates,
	]);

	return {
		disabledDepartureDates,
		disabledReturnDates,
		isLoading,
		maxDepartureDate,
		maxReturnDate,
		minDepartureDate,
		minReturnDate,
		outboundAvailableDates,
		inboundAvailableDates,
		selectedPriceOutbound:
			outboundAvailableDates.find((schedule) =>
				schedule.departureDate.isSame(props.currentSearchManager.currentSearch.departureDate, "day")
			)?.price || "",
		selectedPriceInbound:
			inboundAvailableDates.find((schedule) =>
				schedule.departureDate.isSame(props.currentSearchManager.currentSearch.returnDate, "day")
			)?.price || "",
		setMinReturnDate: (date: dayjs.Dayjs) => setMinReturnDate(date),
	};
};
